import { getPeriodRange } from './callHistory';

/**
 * The call history's filters as one Mongo filter for the paged route
 * (`POST /api/entities/ParallelCall/page`, W4, T-d3). The screen used to
 * filter the 500 calls the dialer had loaded; the server now filters every
 * call of the organisation, so these rules must say on the server exactly
 * what the screen used to say in the browser:
 *
 * - status: the shown status is the first non-empty of call_outcome,
 *   outcome, call_status (getEffectiveOutcome), and a « no_answer » that
 *   lasted 2 min or more is shown as « contacted »;
 * - direction: a row without `direction` is outbound (rows before
 *   2026-09-27), `call_status: inbound` is inbound;
 * - list: the phones of the prospects of the chosen lists;
 * - search: name, company, or the digits of the phone.
 *
 * The tenant and customer scope stays the server's (scopeReadFilter); the
 * selected customer is narrowed here like useDialerDerivedData did.
 */
export interface HistoryFilters {
  period: string;
  customStart?: string;
  customEnd?: string;
  statuses: string[];
  /** Phones of the prospects of the chosen lists; null: no list filter. */
  listPhones: string[] | null;
  minDuration: number;
  direction: 'all' | 'inbound' | 'outbound';
  search: string;
  /** agent_email; '' for everyone. */
  agentEmail: string;
  customerId?: string | null;
}

export const DEFAULT_HISTORY_PERIOD = 'last_7_days';

const EMPTY = { $in: [null, ''] };

/** The stored raw status equals `s` (first non-empty of call_outcome, outcome, call_status). */
function rawStatusIs(s: string): Record<string, unknown> {
  return {
    $or: [
      { call_outcome: s },
      { call_outcome: EMPTY, outcome: s },
      { call_outcome: EMPTY, outcome: EMPTY, call_status: s },
    ],
  };
}

/** One shown status, as getEffectiveOutcome resolves it. */
export function statusClause(s: string): Record<string, unknown> {
  if (s === 'no_answer') return { $and: [rawStatusIs('no_answer'), { duration_seconds: { $not: { $gte: 120 } } }] };
  if (s === 'contacted') return { $or: [rawStatusIs('contacted'), { $and: [rawStatusIs('no_answer'), { duration_seconds: { $gte: 120 } }] }] };
  return rawStatusIs(s);
}

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function buildHistoryFilter(f: HistoryFilters, now: Date = new Date()): Record<string, unknown> {
  const and: Record<string, unknown>[] = [];
  const [start, end] = getPeriodRange(f.period, f.customStart, f.customEnd, now);
  if (start || end) {
    const range: Record<string, string> = {};
    if (start) range.$gte = start.toISOString();
    // A period that runs until now has no upper bound: the query stays the
    // same while the screen is open (same cursor), and new calls still come in.
    if (end && end.getTime() !== now.getTime()) range.$lte = end.toISOString();
    and.push({ created_date: range });
  }
  const [onlyStatus, ...moreStatuses] = f.statuses;
  if (onlyStatus !== undefined && moreStatuses.length === 0) and.push(statusClause(onlyStatus));
  else if (moreStatuses.length > 0) and.push({ $or: f.statuses.map(statusClause) });
  if (f.listPhones) and.push({ prospect_phone: { $in: f.listPhones } });
  if (f.minDuration > 0) and.push({ duration_seconds: { $gte: f.minDuration } });
  if (f.direction === 'inbound') and.push({ $or: [{ direction: 'inbound' }, { call_status: 'inbound' }] });
  if (f.direction === 'outbound') and.push({ direction: { $ne: 'inbound' }, call_status: { $ne: 'inbound' } });
  if (f.agentEmail) and.push({ agent_email: f.agentEmail });
  if (f.customerId) and.push({ customer_id: f.customerId });
  const q = f.search.trim();
  if (q) {
    const text = { $regex: escapeRegex(q), $options: 'i' };
    const or: Record<string, unknown>[] = [{ prospect_name: text }, { prospect_company: text }];
    const digits = q.replace(/\D/g, '');
    // A phone matches on its digits whatever its spacing: « 06 12 » finds +33612…
    if (digits.length >= 3) or.push({ prospect_phone: { $regex: digits.split('').join('\\D*') } });
    and.push({ $or: or });
  }
  const [first, ...rest] = and;
  if (!first) return {};
  if (rest.length === 0) return first;
  return { $and: and };
}

/** How many filters differ from the defaults (the « Effacer les filtres » badge). */
export function activeFilterCount(f: HistoryFilters): number {
  return [
    f.period !== DEFAULT_HISTORY_PERIOD,
    f.statuses.length > 0,
    f.listPhones !== null,
    f.minDuration > 0,
    f.direction !== 'all',
    f.search.trim().length > 0,
    f.agentEmail !== '',
  ].filter(Boolean).length;
}
